/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { weightSemiBold } from 'src/components/css'

type EmphasisTextProps = {
  /** The whole sentence, already translated and interpolated. */
  text: string
  /**
   * The parts to set in semibold white, wherever the translation put them — a
   * team and a season name, say. Empty and missing parts are skipped.
   */
  emphasis: readonly (string | null | undefined)[]
}

/**
 * A translated sentence with some of its interpolated values in semibold: the
 * names a reader scans for. The same idea as the Invited tooltip's address
 * (`BalanceStatusChip`), for any number of parts. The sentence stays one string
 * in i18n, so a translation is free to reorder it.
 */
const EmphasisText = ({ text, emphasis }: EmphasisTextProps) => {
  const parts = emphasis.filter((part): part is string => Boolean(part))
  const nodes: ReactNode[] = []
  let rest = text

  while (rest.length > 0) {
    // The earliest of the parts still ahead, so they come out in reading order;
    // the longer one when two start together ("Bears" inside "Bears Cup").
    let at = -1
    let part = ''
    for (const candidate of parts) {
      const index = rest.indexOf(candidate)
      if (
        index >= 0 &&
        (at < 0 ||
          index < at ||
          (index === at && candidate.length > part.length))
      ) {
        at = index
        part = candidate
      }
    }
    if (at < 0) {
      nodes.push(rest)
      break
    }
    if (at > 0) {
      nodes.push(rest.slice(0, at))
    }
    nodes.push(
      <span key={nodes.length} css={emphasisStyle}>
        {part}
      </span>
    )
    rest = rest.slice(at + part.length)
  }

  return <>{nodes}</>
}

export default EmphasisText

const emphasisStyle = css`
  ${weightSemiBold};
  color: ${colors.NEUTRAL_0};
`
